import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getUserGroups } from '../utils/permissions';
import {
  ensureSessionWithRefresh,
  isRefreshFailureError,
} from '../services/authSession';

const useAuthStore = create(devtools((set, get) => ({
  // State
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  authHeaders: null,
  groups: [],

  // Actions
  setUser: (user) => set({
    user,
    isAuthenticated: !!user
  }),

  setTokens: (tokens) => {
    const groups = tokens?.idToken ? getUserGroups(tokens.idToken) : [];
    set({
      tokens,
      groups,
      authHeaders: tokens ? {
        Authorization: `Bearer ${tokens?.idToken?.toString()}`
      } : null
    });
  },

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  clearError: () => set({ error: null }),

  // Async actions
  getAuthHeaders: async () => {
    try {
      const session = await ensureSessionWithRefresh();
      const tokens = session.tokens;
      if (!tokens?.idToken) {
        throw new Error('Authentication failed. Please sign in again.');
      }
      const authHeaders = {
        Authorization: `Bearer ${tokens.idToken.toString()}`
      };

      const groups = getUserGroups(tokens.idToken);
      set({
        tokens,
        groups,
        authHeaders,
        error: null
      });

      return authHeaders;
    } catch (error) {
      if (isRefreshFailureError(error)) {
        set({
          error,
          authHeaders: null,
          tokens: null,
          isAuthenticated: false
        });
      } else {
        set({ error });
      }
      throw error;
    }
  },

  refreshSession: async () => {
    try {
      set({ isLoading: true, error: null });

      const session = await ensureSessionWithRefresh();
      const tokens = session.tokens;
      const groups = tokens?.idToken ? getUserGroups(tokens.idToken) : [];
      const authHeaders = tokens ? {
        Authorization: `Bearer ${tokens.idToken.toString()}`
      } : null;

      set({
        tokens,
        groups,
        authHeaders,
        isAuthenticated: !!tokens,
        isLoading: false,
        error: null
      });

      return tokens;
    } catch (error) {
      if (isRefreshFailureError(error)) {
        set({
          error,
          tokens: null,
          authHeaders: null,
          isAuthenticated: false,
          isLoading: false
        });
      } else {
        set({
          error,
          isLoading: false
        });
      }
      throw error;
    }
  },

  signOut: () => {
    set({
      user: null,
      tokens: null,
      groups: [],
      authHeaders: null,
      isAuthenticated: false,
      error: null
    });
  },

  // Initialize auth on app start
  initializeAuth: async () => {
    const { refreshSession } = get();
    try {
      await refreshSession();
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      set({ isLoading: false });
    }
  }
}), {
  name: 'auth-store',
}));

export default useAuthStore;